class PMVideoTestimonials {
  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    this.root = root;
    /** @type {HTMLElement[]} */
    this.slides = [...root.querySelectorAll('.pm-vt__card')];
    /** @type {number} */
    this.activeIndex = Number(root.dataset.activeIndex || 0);
    /** @type {HTMLVideoElement | null} */
    this.playingVideo = null;

    if (this.slides.length === 0) return;

    if (this.activeIndex >= this.slides.length) {
      this.activeIndex = this.slides.length > 2 ? 1 : 0;
    }

    this.updateSlides();

    root.querySelector('[data-action="prev"]')?.addEventListener('click', () => this.go(-1));
    root.querySelector('[data-action="next"]')?.addEventListener('click', () => this.go(1));

    root.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      const playButton = target.closest('[data-action="play"]');
      if (!playButton) return;

      const card = playButton.closest('.pm-vt__card');
      const video = card?.querySelector('video');
      if (!(video instanceof HTMLVideoElement)) return;

      this.toggleVideo(video, playButton);
    });

    this.bindSwipe();
    window.addEventListener('resize', () => this.updateSlides());
  }

  /**
   * @param {number} direction
   */
  go(direction) {
    const next = this.activeIndex + direction;
    if (next < 0 || next >= this.slides.length) return;

    this.pauseAllVideos();
    this.activeIndex = next;
    this.root.dataset.activeIndex = String(this.activeIndex);
    this.updateSlides();
  }

  updateSlides() {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;

    this.slides.forEach((slide, index) => {
      slide.classList.remove(
        'is-active',
        'is-prev',
        'is-next',
        'is-hidden',
        'is-hidden-left',
        'is-hidden-right'
      );

      if (isMobile) {
        slide.classList.toggle('is-active', index === this.activeIndex);
        if (index !== this.activeIndex) {
          slide.classList.add('is-hidden');
          slide.classList.add(index < this.activeIndex ? 'is-hidden-left' : 'is-hidden-right');
        }
        return;
      }

      if (index === this.activeIndex) {
        slide.classList.add('is-active');
      } else if (index === this.activeIndex - 1) {
        slide.classList.add('is-prev');
      } else if (index === this.activeIndex + 1) {
        slide.classList.add('is-next');
      } else {
        slide.classList.add('is-hidden');
        slide.classList.add(index < this.activeIndex ? 'is-hidden-left' : 'is-hidden-right');
      }
    });

    const prevButton = this.root.querySelector('[data-action="prev"]');
    const nextButton = this.root.querySelector('[data-action="next"]');

    if (prevButton instanceof HTMLButtonElement) {
      prevButton.disabled = this.activeIndex === 0;
    }

    if (nextButton instanceof HTMLButtonElement) {
      nextButton.disabled = this.activeIndex === this.slides.length - 1;
    }
  }

  /**
   * @param {HTMLVideoElement} video
   * @param {Element} playButton
   */
  toggleVideo(video, playButton) {
    if (!video.paused) {
      video.pause();
      playButton.classList.remove('is-playing');
      this.playingVideo = null;
      return;
    }

    this.pauseAllVideos();
    video.muted = false;
    video
      .play()
      .then(() => {
        playButton.classList.add('is-playing');
        this.playingVideo = video;
      })
      .catch(() => {
        video.muted = true;
        video.play();
        playButton.classList.add('is-playing');
        this.playingVideo = video;
      });
  }

  pauseAllVideos() {
    this.root.querySelectorAll('video').forEach((video) => {
      if (video instanceof HTMLVideoElement) {
        video.pause();
        video.muted = true;
      }
    });

    this.root.querySelectorAll('[data-action="play"]').forEach((button) => {
      button.classList.remove('is-playing');
    });

    this.playingVideo = null;
  }

  bindSwipe() {
    const track = this.root.querySelector('[data-track]');
    if (!(track instanceof HTMLElement)) return;

    let startX = 0;

    track.addEventListener(
      'touchstart',
      (event) => {
        startX = event.touches[0]?.clientX ?? 0;
      },
      { passive: true }
    );

    track.addEventListener(
      'touchend',
      (event) => {
        const endX = event.changedTouches[0]?.clientX ?? 0;
        const delta = endX - startX;

        if (Math.abs(delta) < 40) return;
        this.go(delta > 0 ? -1 : 1);
      },
      { passive: true }
    );
  }
}

function initVideoTestimonials() {
  document.querySelectorAll('[data-pm-video-testimonials]').forEach((root) => {
    if (!(root instanceof HTMLElement) || root.dataset.pmVtReady === 'true') return;
    root.dataset.pmVtReady = 'true';
    new PMVideoTestimonials(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVideoTestimonials);
} else {
  initVideoTestimonials();
}

document.addEventListener('shopify:section:load', initVideoTestimonials);
